import React from 'react'
import {Trans, useTranslation} from 'react-i18next'

import {Box, CircularProgress, Grid, Typography} from '@mui/material'

import {MenopauseAppointmentItem} from '@progyny/pages/Consultations/Menopause/useMenopauseConsultationAppointments'
import AppointmentCard from '@progyny/pages/Consultations/components/AppointmentCard'
import CancelConsultation from '@progyny/pages/Consultations/components/CancelConsultation'
import {
  formatAppointmentDateWithWeekday,
  formatAppointmentTimeRange,
} from '@progyny/utils/appointmentHelpers'

export interface AppointmentListProps {
  items: MenopauseAppointmentItem[]
  colors: any
  status: 'upcoming' | 'completed'
  loading?: boolean
  consultationSlug?: string
  onCancelBack?: () => void
}

const AppointmentList: React.FC<AppointmentListProps> = ({
  items,
  colors,
  status,
  loading,
  consultationSlug,
  onCancelBack,
}) => {
  const {t} = useTranslation()
  const [activeCancelUrl, setActiveCancelUrl] = React.useState<string | null>(
    null
  )

  const handleBack = React.useCallback(() => {
    setActiveCancelUrl(null)
    if (onCancelBack) {
      onCancelBack()
    }
  }, [onCancelBack])

  if (loading) {
    return (
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          minHeight: 200,
          width: '100%',
        }}
        aria-busy
      >
        <CircularProgress color='primary' size={32} />
      </Box>
    )
  }

  if (status === 'upcoming' && activeCancelUrl) {
    return (
      <CancelConsultation cancelUrl={activeCancelUrl} onBack={handleBack} />
    )
  }

  if (items.length === 0) {
    return (
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          minHeight: 200,
          width: '100%',
        }}
      >
        <Typography>
          {status === 'upcoming' ? (
            <Trans i18nKey='consultations_program_upcoming_sessions_no_upcoming_sessions' />
          ) : (
            <Trans i18nKey='consultations_single_session_consultation_view_no_past_consultations' />
          )}
        </Typography>
      </Box>
    )
  }

  return (
    <Grid container rowGap={2}>
      {items.map((item, index) => (
        <Grid item xs={12} key={item.id || index}>
          <AppointmentCard
            status={status === 'upcoming' ? 'upcoming' : 'completed'}
            session={
              item.type === 'emotional_program' && item.sessionNumber
                ? `${t('common_session')} ${item.sessionNumber}`
                : undefined
            }
            title={
              item.type === 'onboarding'
                ? t('consultations_onboarding_section_your_onboarding_call')
                : item.title
            }
            subtitle=''
            date={formatAppointmentDateWithWeekday(item.scheduledTime)}
            time={formatAppointmentTimeRange(item.scheduledTime, 30)}
            description={item.description}
            colors={colors}
            programSlug={item.programSlug}
            sessionNumber={item.sessionNumber}
            appointment={item.appointment}
            hideStatus
            consultationTypeLabel={
              item.type === 'emotional_program'
                ? t('consultations_upcoming_filter_emotional_program')
                : t('consultations_upcoming_filter_general')
            }
            consultationType={
              item.type === 'emotional_program'
                ? 'emotional_program'
                : 'general'
            }
            consultationSlug={
              item.type === 'onboarding' ? consultationSlug : undefined
            }
            onCancelRequested={
              status === 'upcoming'
                ? (url) => setActiveCancelUrl(url)
                : undefined
            }
          />
        </Grid>
      ))}
    </Grid>
  )
}

export default AppointmentList
